import React, { useState, useEffect } from 'react';
import {
  Form,
  Card,
  Row,
  Col,
  Select,
  Button,
  Table,
  InputNumber,
  Divider,
  message,
  Input,
  Spin,
  Alert,
  Tag
} from 'antd';
import ProductSelector from './product_selector';
import axiosInstance from '../../../axiosInstance';

const ProductTransferForm = ({ onTransferCreated }) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connectedShops, setConnectedShops] = useState([]);
  const [currentShop, setCurrentShop] = useState(null);
  const [fetchingShops, setFetchingShops] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConnectedShops = async () => {
      try {
        const response = await axiosInstance.get('/shops/connected-shops');
        setCurrentShop(response.data.current_shop);
        setConnectedShops(response.data.shops || []);

        if (!response.data.shops || response.data.shops.length === 0) {
          setError('No connected shops available for product transfer');
        }
      } catch (error) {
        setError('Failed to load connected shops');
        console.error('Error fetching connected shops:', error);
      } finally {
        setFetchingShops(false);
      }
    };

    fetchConnectedShops();
  }, []);

  const handleFinish = async (values) => {
    if (products.length === 0) {
      message.warning('Please add at least one product');
      return;
    }

    // Validate all products have quantity
    const invalidProducts = products.filter(p => !p.quantity || p.quantity < 1);
    if (invalidProducts.length > 0) {
      message.error('Please set valid quantities for all products');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post('/transfers', {
        source_shop_id: currentShop.id,
        target_shop_id: values.target_shop_id,
        notes: values.notes,
        items: products.map(p => ({
          product_id: p.id,
          barcode: p.barcode,
          quantity_requested: p.quantity,
          quantity: p.quantity // Include stock for backend validation
        }))
      });

      message.success('Transfer created successfully');
      form.resetFields();
      setProducts([]);
      onTransferCreated?.(response.data.transfer);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create transfer';
      const fieldErrors = error.response?.data?.errors;

      if (fieldErrors) {
        Object.entries(fieldErrors).forEach(([field, messages]) => {
          messages.forEach(msg => message.error(`${field}: ${msg}`));
        });
      } else {
        message.error(errorMessage);
      }
      console.error('Transfer creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = (product) => {
    if (product.quantity < 1) {
      message.error('Cannot add out-of-stock product');
      return;
    }

    const existingIndex = products.findIndex(p => p.barcode === product.barcode);
    if (existingIndex >= 0) {
      const updatedProducts = [...products];
      updatedProducts[existingIndex].quantity += product.quantity;
      setProducts(updatedProducts);
    } else {
      setProducts([...products, {
        ...product,
        quantity: product.quantity || 1
      }]);
    }
  };

  const removeProduct = (barcode) => {
    setProducts(products.filter(p => p.barcode !== barcode));
  };

  const updateQuantity = (index, value) => {
    if (value < 1) {
      message.error('Quantity must be at least 1');
      return;
    }

    if (value > products[index].quantity) {
      message.error('Quantity cannot exceed available stock');
      return;
    }

    const newProducts = [...products];
    newProducts[index].quantity = value;
    setProducts(newProducts);
  };

  if (fetchingShops) {
    return <Spin size="large" />;
  }

  if (error) {
    return (
      <Card title="Create New Product Transfer">
        <Alert message={error} type="error" showIcon />
      </Card>
    );
  }

  return (
    <Card title="Create New Product Transfer" bordered={false}>
      <Form form={form} onFinish={handleFinish} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="From Shop">
              <Input
                value={currentShop?.name || 'Current Shop'}
                disabled
                addonBefore={currentShop?.type ? `${currentShop.type}:` : ''}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="target_shop_id"
              label="To Shop"
              rules={[{
                required: true,
                message: 'Please select target shop'
              }]}
            >
              <Select
                placeholder="Select target shop"
                loading={fetchingShops}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {connectedShops.map(shop => (
                  <Select.Option key={shop.id} value={shop.id}>
                    {shop.name} ({shop.type || 'shop'}) - {shop.location}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea placeholder="Optional notes about this transfer" rows={3} />
        </Form.Item>

        <ProductSelector
          onAddProduct={addProduct}
          currentShopId={currentShop?.id}
        />

        {products.length > 0 && (
          <>
            <Divider orientation="left">Products to Transfer</Divider>
            <Table
              columns={[
                { title: 'Barcode', dataIndex: 'barcode' },
                { title: 'Name', dataIndex: 'name' },
                {
                  title: 'Available Stock',
                  dataIndex: 'quantity',
                  render: (stock) => (
                    <Tag color={stock > 0 ? 'green' : 'red'}>
                      {stock}
                    </Tag>
                  )
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  render: (value, record, index) => (
                    <InputNumber
                      min={1}
                      max={record.quantity}
                      value={value}
                      onChange={val => updateQuantity(index, val)}
                      style={{ width: '100%' }}
                    />
                  )
                },
                {
                  title: 'Action',
                  width: 100,
                  render: (_, record) => (
                    <Button
                      danger
                      onClick={() => removeProduct(record.barcode)}
                      size="small"
                    >
                      Remove
                    </Button>
                  )
                }
              ]}
              dataSource={products}
              rowKey="barcode"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              style={{ maxWidth: '100%' }}
            />
          </>
        )}

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={products.length === 0 || fetchingShops}
            size="large"
          >
            Create Transfer
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ProductTransferForm;